import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { usePremium } from './PremiumContext';
import { useAuth } from './AuthContext';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown, Sparkles, Check, Flame, Clock, Zap } from 'lucide-react';

const PaywallContext = createContext(null);

export const usePaywall = () => {
  const ctx = useContext(PaywallContext);
  if (!ctx) return { openPaywall: () => {}, closePaywall: () => {} };
  return ctx;
};

// More frequent: re-show at most once per this cooldown (minutes) instead of once/day
const PAYWALL_COOLDOWN_MIN = 90;
const INTERACTION_THRESHOLD = 4;      // show after N in-app navigations
const OPEN_DELAY_MS = 3000;           // delay after app open

const APP_ROUTES = [
  '/dashboard', '/scanner', '/nutrition', '/meals',
  '/workouts', '/progress', '/profile', '/social', '/bariatric',
];

const REF_PRICE = '23,99 €';          // ancien prix (pour l'effet "prix barré")
const DISCOUNT = '-67%';

const BENEFITS = [
  'IA illimitée (repas & conseils)',
  '+32 000 recettes exclusives',
  "+1 600 vidéos d'entraînement",
  'Statistiques avancées & support prioritaire',
];

export const PaywallProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isPremium, getFormattedPrice, productInfo } = usePremium();

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('daily');
  const [variant, setVariant] = useState('A');
  const interactionCount = useRef(0);
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const price = getFormattedPrice ? (getFormattedPrice() || '7,99 €') : '7,99 €';
  // The crossed-out reference (23,99 €) and -67% are EUR-only. For other currencies,
  // Google's local price is authoritative and there is no EUR reference -> hide them.
  const currency = productInfo?.price?.currency || 'EUR';
  const isEUR = currency === 'EUR';

  const canShowAuto = () => {
    try {
      const last = parseInt(localStorage.getItem('paywall_last_ts') || '0', 10);
      return (Date.now() - last) > PAYWALL_COOLDOWN_MIN * 60 * 1000;
    } catch (e) { return true; }
  };
  const markShown = () => {
    try { localStorage.setItem('paywall_last_ts', String(Date.now())); } catch (e) { /* ignore */ }
  };
  const pickVariant = () => {
    try {
      const n = parseInt(localStorage.getItem('paywall_variant_n') || '0', 10) + 1;
      localStorage.setItem('paywall_variant_n', String(n));
      return n % 2 === 0 ? 'A' : 'B';
    } catch (e) { return Math.random() < 0.5 ? 'A' : 'B'; }
  };

  const openPaywall = useCallback((r = 'daily', forcedVariant = null) => {
    if (isPremium || !user) return;
    if ((locationRef.current || '').startsWith('/premium')) return;
    setReason(r);
    setVariant(forcedVariant || pickVariant());
    setOpen(true);
  }, [isPremium, user]);

  const closePaywall = useCallback(() => setOpen(false), []);

  // DEV/validation: force a variant via ?_pw=A or ?_pw=B
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const forced = params.get('_pw');
    if (forced === 'A' || forced === 'B') {
      setReason('daily');
      setVariant(forced);
      setOpen(true);
    }
  }, [location.search]);

  // 1) On app open (more frequent: cooldown-based, not once/day)
  useEffect(() => {
    if (!user || isPremium) return;
    if (!canShowAuto()) return;
    const t = setTimeout(() => {
      if (canShowAuto() && !isPremium) { openPaywall('daily'); markShown(); }
    }, OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, [user, isPremium, openPaywall]);

  // 2) After a few interactions
  useEffect(() => {
    if (!user || isPremium) return;
    if (!APP_ROUTES.some((p) => location.pathname.startsWith(p))) return;
    interactionCount.current += 1;
    if (interactionCount.current >= INTERACTION_THRESHOLD && canShowAuto() && !isPremium) {
      interactionCount.current = 0;
      openPaywall('interactions'); markShown();
    }
  }, [location.pathname, user, isPremium, openPaywall]);

  // 3) AI / free-tier limit (HTTP 429)
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        try {
          if (error?.response?.status === 429 && user && !isPremium) {
            openPaywall('ai_limit');
          }
        } catch (e) { /* ignore */ }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [user, isPremium, openPaywall]);

  const goPremium = () => { setOpen(false); navigate('/premium'); };

  return (
    <PaywallContext.Provider value={{ openPaywall, closePaywall }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm overflow-hidden p-0 border-0" data-testid="paywall-modal">
          <DialogTitle className="sr-only">Offre Premium Fat & Slim</DialogTitle>

          {variant === 'A' ? (
            /* ===== VARIANT A — Urgence / Prix de lancement ===== */
            <div data-testid="paywall-variant-a" className="bg-gradient-to-b from-[#2a1005] to-[#1a1a2e] text-white p-6">
              <div className="flex justify-center">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold px-3 py-1 animate-pulse">
                  <Clock className="w-3.5 h-3.5" /> OFFRE EXCEPTIONNELLE LIMITÉE
                </span>
              </div>
              <h2 className="text-center text-2xl font-extrabold mt-4 leading-tight">
                Prix de lancement<br/>exceptionnel <Flame className="inline w-6 h-6 text-orange-400" />
              </h2>
              <div className="flex items-baseline justify-center gap-2 mt-3">
                {isEUR && <span className="text-white/50 line-through text-lg">{REF_PRICE}</span>}
                <span className="text-4xl font-black text-orange-400">{price}</span>
                <span className="text-white/70">/mois</span>
              </div>
              {isEUR && (
                <div className="text-center mt-1">
                  <span className="inline-block bg-orange-500 text-black text-sm font-extrabold rounded-md px-2 py-0.5">{DISCOUNT} MAINTENANT</span>
                </div>
              )}
              <div className="mt-4 space-y-1.5">
                {BENEFITS.map((b) => (
                  <div key={b} className="flex items-center gap-2 text-sm text-white/90">
                    <Check className="w-4 h-4 text-orange-400 flex-shrink-0" /> {b}
                  </div>
                ))}
              </div>
              <Button
                className="w-full h-14 text-lg font-extrabold mt-5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg"
                onClick={goPremium}
                data-testid="paywall-cta-btn"
              >
                <Zap className="w-5 h-5 mr-2" /> {"S'abonner maintenant"}
              </Button>
              <p className="text-center text-xs text-white/60 mt-2">7 jours gratuits · sans engagement · annulable à tout moment</p>
              <button className="w-full text-center text-white/40 text-sm mt-2" onClick={closePaywall} data-testid="paywall-later-btn">Plus tard</button>
            </div>
          ) : (
            /* ===== VARIANT B — Premium / Aspirationnel ===== */
            <div data-testid="paywall-variant-b" className="bg-gradient-to-b from-[#0e2a1a] to-[#1a1a2e] text-white p-6">
              <div className="flex justify-center mb-1">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg">
                  <Crown className="w-9 h-9 text-[#0e2a1a]" />
                </div>
              </div>
              <div className="flex justify-center mt-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/20 border border-green-500/40 text-green-300 text-xs font-bold px-3 py-1">
                  <Sparkles className="w-3.5 h-3.5" /> OFFRE DE LANCEMENT LIMITÉE
                </span>
              </div>
              <h2 className="text-center text-2xl font-extrabold mt-3 leading-tight">Passez Premium maintenant</h2>
              <p className="text-center text-white/70 text-sm mt-1">Débloquez tout Fat &amp; Slim, sans limite.</p>
              <div className="flex items-baseline justify-center gap-2 mt-3">
                {isEUR && <span className="text-white/45 line-through">{REF_PRICE}</span>}
                <span className="text-4xl font-black text-green-400">{price}</span>
                <span className="text-white/70">/mois</span>
              </div>
              <div className="mt-4 space-y-1.5">
                {BENEFITS.map((b) => (
                  <div key={b} className="flex items-center gap-2 text-sm text-white/90">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" /> {b}
                  </div>
                ))}
              </div>
              <Button
                className="w-full h-14 text-lg font-extrabold mt-5 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-[#06120c] shadow-lg"
                onClick={goPremium}
                data-testid="paywall-cta-btn"
              >
                <Crown className="w-5 h-5 mr-2" /> {"S'abonner maintenant"}
              </Button>
              <p className="text-center text-xs text-white/60 mt-2">7 jours gratuits · sans engagement · annulable à tout moment</p>
              <button className="w-full text-center text-white/40 text-sm mt-2" onClick={closePaywall} data-testid="paywall-later-btn">Plus tard</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PaywallContext.Provider>
  );
};

export default PaywallContext;
